import { capacitorDbService } from './capacitorDb.service';

// ============================================================
// NurWallet AI Gateway Service
// Replaces capacitorAI.service.ts - calls the AI Gateway API
// ============================================================

// UPDATE THIS with your deployed API URL
const NURWALLET_API_URL = 'https://qgqrwqo2ka5ok.kimi.page';

export interface AIChatResponse {
  content: string;
  modelUsed: string;
  provider: string;
  tokensUsed: number;
  latencyMs: number;
  isOffline: boolean;
  sessionId?: number;
}

class AIGatewayService {
  private authToken: string = '';
  private initialized = false;

  async init(authToken?: string): Promise<void> {
    if (this.initialized) return;
    if (authToken) this.authToken = authToken;
    this.initialized = true;
    console.log('[AIGateway] Initialized');
  }

  setAuthToken(token: string): void {
    this.authToken = token;
  }

  // MAIN: Chat with AI Gateway (with RAG)
  async chat(
    message: string,
    userId: string,
    options: {
      sessionId?: number;
      useRAG?: boolean;
      history?: Array<{ role: string; content: string }>;
    } = {}
  ): Promise<AIChatResponse> {
    const { sessionId, useRAG = true, history = [] } = options;

    try {
      const userContext = await this.buildUserContext(userId);

      const response = await fetch(`${NURWALLET_API_URL}/api/trpc/ai.chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.authToken ? { Authorization: `Bearer ${this.authToken}` } : {}),
        },
        body: JSON.stringify({
          json: {
            message,
            sessionId,
            useRAG,
            userContext,
            history: history.slice(-5),
          },
        }),
      });

      if (!response.ok) {
        return this.localFallback(message, userContext);
      }

      const result = await response.json();
      const data = result.result?.data;

      if (!data) {
        return this.localFallback(message, userContext);
      }

      return {
        content: data.response,
        modelUsed: data.modelUsed,
        provider: data.provider,
        tokensUsed: data.tokensUsed,
        latencyMs: data.latencyMs,
        isOffline: data.isOffline,
        sessionId: data.sessionId,
      };
    } catch (error: any) {
      console.error('[AIGateway] Chat error:', error.message);
      const userContext = await this.buildUserContext(userId);
      return this.localFallback(message, userContext);
    }
  }

  // RAG: Search knowledge base
  async searchKnowledge(query: string, category?: string): Promise<any[]> {
    try {
      const url = new URL(`${NURWALLET_API_URL}/api/trpc/knowledge.search`);
      url.searchParams.set('input', JSON.stringify({ json: { query, category, limit: 10 } }));

      const response = await fetch(url.toString());
      if (!response.ok) return [];

      const result = await response.json();
      return result.result?.data || [];
    } catch (error) {
      console.error('[AIGateway] Knowledge search error:', error);
      return [];
    }
  }

  // Get available AI models
  async getModels(): Promise<any[]> {
    try {
      const response = await fetch(`${NURWALLET_API_URL}/api/trpc/ai.models`);
      if (!response.ok) return [];

      const result = await response.json();
      return result.result?.data || [];
    } catch (error) {
      console.error('[AIGateway] Models error:', error);
      return [];
    }
  }

  // Health check
  async healthCheck(): Promise<any[]> {
    try {
      const response = await fetch(`${NURWALLET_API_URL}/api/trpc/ai.healthCheck`);
      if (!response.ok) return [];

      const result = await response.json();
      return result.result?.data || [];
    } catch (error) {
      console.error('[AIGateway] Health check error:', error);
      return [];
    }
  }

  // Build user context from LOCAL SQLite
  private async buildUserContext(userId: string): Promise<any> {
    try {
      const [
        assetSummary,
        liabilitySummary,
        txSummary,
        zakatRecords,
      ] = await Promise.all([
        capacitorDbService.getAssetSummary(userId),
        capacitorDbService.getLiabilitySummary(userId),
        capacitorDbService.getTransactionSummary(userId),
        capacitorDbService.getZakatRecords(userId).catch(() => []),
      ]);

      const totalAssets = assetSummary?.totalValue || 0;
      const totalLiabilities = liabilitySummary?.totalRemaining || 0;
      const monthlyPayments = liabilitySummary?.monthlyPayments || 0;

      return {
        netWorth: totalAssets - totalLiabilities,
        totalAssets,
        totalLiabilities,
        monthlyIncome: txSummary?.totalIncome || 0,
        monthlyExpenses: txSummary?.totalExpense || 0,
        monthlySavingsRate: txSummary?.totalIncome > 0
          ? (txSummary.totalIncome - txSummary.totalExpense) / txSummary.totalIncome
          : 0,
        monthlyPayments,
        debtToIncomeRatio: txSummary?.totalIncome > 0
          ? monthlyPayments / (txSummary.totalIncome / 12)
          : 0,
        zakatLiability: zakatRecords[0]?.zakatPayable || 0,
        healthScore: this.calculateHealthScore({
          netWorth: totalAssets - totalLiabilities,
          savingsRate: txSummary?.totalIncome > 0
            ? (txSummary.totalIncome - txSummary.totalExpense) / txSummary.totalIncome
            : 0,
          debtToIncome: txSummary?.totalIncome > 0 ? monthlyPayments / (txSummary.totalIncome / 12) : 0,
        }),
      };
    } catch (error) {
      console.error('[AIGateway] Build context error:', error);
      return {};
    }
  }

  // LOCAL FALLBACK: When API is offline
  private localFallback(message: string, userContext: any): AIChatResponse {
    const lowerMsg = message.toLowerCase();

    if (lowerMsg.includes('zakat')) {
      return { content: this.buildZakatResponse(userContext), modelUsed: 'offline-local', provider: 'local', tokensUsed: 0, latencyMs: 0, isOffline: true };
    }
    if (lowerMsg.includes('retirement') || lowerMsg.includes('epf') || lowerMsg.includes('kwsp')) {
      return { content: this.buildRetirementResponse(userContext), modelUsed: 'offline-local', provider: 'local', tokensUsed: 0, latencyMs: 0, isOffline: true };
    }
    if (lowerMsg.includes('takaful') || lowerMsg.includes('insurance')) {
      return { content: this.buildTakafulResponse(userContext), modelUsed: 'offline-local', provider: 'local', tokensUsed: 0, latencyMs: 0, isOffline: true };
    }

    return {
      content: `I apologize, but I'm currently offline and unable to connect to the AI Gateway.\n\nYour Financial Snapshot:\n- Net Worth: RM ${(userContext?.netWorth || 0).toLocaleString()}\n- Total Assets: RM ${(userContext?.totalAssets || 0).toLocaleString()}\n- Total Liabilities: RM ${(userContext?.totalLiabilities || 0).toLocaleString()}\n- Monthly Income: RM ${(userContext?.monthlyIncome || 0).toLocaleString()}\n\nPlease check your internet connection and try again.`,
      modelUsed: 'offline-local', provider: 'local', tokensUsed: 0, latencyMs: 0, isOffline: true,
    };
  }

  private buildZakatResponse(ctx: any): string {
    const nisab = 28050;
    const zakatableWealth = (ctx?.totalAssets || 0) * 0.7;
    const zakatDue = zakatableWealth >= nisab ? zakatableWealth * 0.025 : 0;
    let response = `**Zakat Summary (Offline Mode)**\n\n- Zakatable Wealth: RM ${Math.round(zakatableWealth).toLocaleString()}\n- Nisab Threshold: RM ${nisab.toLocaleString()}\n`;
    if (zakatDue > 0) {
      response += `- **Zakat Due (2.5%): RM ${Math.round(zakatDue).toLocaleString()}**\n\nYou can pay zakat to PPZ-MAIWP, LZS, or your state zakat center. Zakat payment is tax-deductible in Malaysia.`;
    } else {
      response += `- No Zakat Due\n\nYour wealth is below the Nisab threshold. Sadaqah (voluntary charity) is always encouraged.`;
    }
    return response;
  }

  private buildRetirementResponse(ctx: any): string {
    return `**Retirement Planning (Offline Mode)**\n\n- Current Net Worth: RM ${(ctx?.netWorth || 0).toLocaleString()}\n- Monthly Savings Rate: ${((ctx?.monthlySavingsRate || 0) * 100).toFixed(1)}%\n\n**Key Recommendations:**\n- Ensure EPF contributions (11% employee + 12-13% employer)\n- Consider PRS for tax relief up to RM 3,000/year\n- Diversify with Shariah-compliant unit trusts and Sukuk\n- Aim to save 15-20% of income for retirement\n\nTarget: RM 900,000 - 1,500,000 for comfortable retirement`;
  }

  private buildTakafulResponse(ctx: any): string {
    const annualIncome = (ctx?.monthlyIncome || 0) * 12;
    const recommended = annualIncome * 8;
    return `**Takaful Protection (Offline Mode)**\n\n- Annual Income: RM ${annualIncome.toLocaleString()}\n- Recommended Coverage: RM ${recommended.toLocaleString()} (8x income)\n\n**Key Coverage Areas:**\n- Family Takaful (Life protection + savings)\n- Medical Takaful (Hospitalization)\n- Critical Illness (36 conditions)\n- Personal Accident\n\n**Takaful Operators:**\n- Syarikat Takaful Malaysia (STMB)\n- Etiqa Takaful\n- Prudential BSN Takaful\n- Great Eastern Takaful`;
  }

  private calculateHealthScore(metrics: any): number {
    let score = 50;
    score += Math.min(25, metrics.savingsRate * 100);
    score += Math.max(0, 15 - metrics.debtToIncome * 100);
    score += metrics.netWorth > 0 ? 10 : 0;
    return Math.min(100, Math.max(0, Math.round(score)));
  }
}

export const aiGatewayService = new AIGatewayService();
export default aiGatewayService;
